/* global fbq */

import React, { useEffect, useState, useRef, useCallback } from "react";
import Header from "./Header";
import { ENV, path } from "../../path.js";
import axios from "axios";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ContestCard from "./ContestCard.js";
import ReactGA from 'react-ga4'
import { EventTypes, trackEvent } from "../../Utils/Analytics";
import { toast } from "react-toastify";
import { getStoredAttribution } from "../../Utils/attribution.js";
import { redirectToStore } from "../../Utils/genralUtility.js";

function Competition() {
  const user = useSelector((state) => state.AuthReducer.user);
  const history = useHistory();

  const [ongoingContests, setOngoingContests] = useState([]);
  const [previousContests, setPreviousContests] = useState([]);
  const [contestType, setContestType] = useState('Ongoing');
  const [loading, setLoading] = useState(false);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const [endedContestCount, setEndedContestCount] = useState();
  const [ongoingContestCount, setOngoingContestCount] = useState();

  const ongoingPage = useRef(1);
  const previousPage = useRef(1);
  const ongoingLoadingRef = useRef(false);
  const previousLoadingRef = useRef(false);
  const ongoingEndedRef = useRef(false);
  const previousEndedRef = useRef(false);

  const scrollRef = useRef();

  const rawSubSkill = new URLSearchParams(window.location.search).get("subskill");
  const urlParamSubSkill = rawSubSkill
    ? rawSubSkill
        .replace(/[_-]/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    : "";


  useEffect(() => {
    if (ENV === "prod") {
      ReactGA.event("contest_list_opened", { user_id: user?._id ?? "anonymous" });
      fbq('trackCustom', "contest_list_opened", { user_id: user?._id ?? "anonymous", email: user?.email || "" });
    }

    if (ENV === "prod") {
      trackEvent(EventTypes.PAGE_VIEW, { page: "contest-list" });
    }

    // Initial fetch if both arrays are empty
    if (ongoingContests.length === 0) {
      getOngoingContests();
    }
    if (previousContests.length === 0) {
      getPreviousContests();
    }
  }, []);

  useEffect(() => {
    setLoading((contestType === "Ongoing")? ongoingLoadingRef.current: previousLoadingRef.current);
    setIsEndOfList((contestType === "Ongoing")? ongoingEndedRef.current: previousEndedRef.current);
  }, [contestType])

  const fetchContests = async (type) => {
    const loadingRef = type === "Ongoing" ? ongoingLoadingRef : previousLoadingRef;
    const pageRef = type === "Ongoing" ? ongoingPage : previousPage;
    const isEndedRef = type === "Ongoing" ? ongoingEndedRef : previousEndedRef;

    if (loadingRef.current || isEndedRef.current) {return;}

    setLoading(true);
    loadingRef.current = true;

    try {
      const paramAttributes = new URLSearchParams(window.location.search);
      const preferredSubSkill = paramAttributes.get("subskill")

      const params = {
        status: type === "Ongoing" ? "ongoing" : "previous",
        page: pageRef.current,
        preferredSubSkill
      };

      const response = await axios.get(`${path}/v2/contest`, { params });

      if (response.data.contests.length === 0) {
        isEndedRef.current = true;
        setIsEndOfList(true);
      } else {
        if (type === "Ongoing") {
          setOngoingContests(prev => [...prev, ...response.data.contests]);
          if(!ongoingContestCount) {
            setOngoingContestCount(response.data.totalCount)
          }
        } else {
          setPreviousContests(prev => [...prev, ...response.data.contests]);
          if(!endedContestCount) {
            setEndedContestCount(response.data.totalCount)
          }
        }
        pageRef.current += 1;
      }
    } catch (err) {
      console.error("Error fetching contests:", err.message);
      toast.error("There was error while loading contests, please try refreshing!")
    } finally {
      setLoading(false)
      loadingRef.current = false;
    }
  };

  const getOngoingContests = async () => {
    await fetchContests("Ongoing");
  };

  const getPreviousContests = async () => {
    await fetchContests("Previous");
  };

  const handleScroll = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const isEndReached = (contestType === "Ongoing" && ongoingEndedRef.current) ||
                           (contestType === "Previous" && previousEndedRef.current);
      if (!isEndReached) {
        fetchContests(contestType);
      }
    }
  }, [contestType]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }

    scrollEl.addEventListener("scroll", handleScroll);
    return () => {
      scrollEl.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const openContestPage = (item) => {
    if(ENV === "prod" && item.subSkill && item.title) {
      const eventSubSkill = item.subSkill.trim().replace(" ","_").toLowerCase() || "";
      const eventName = `contest_${eventSubSkill}`
      const attributes = getStoredAttribution();
      const params = { contestTitle: item.title, ...attributes }
      ReactGA.event(eventName, params);
      fbq('trackCustom', eventName, params);
    }
    history.push(`/contest/${item._id}`);
  };

  const contestsToShow = contestType === "Ongoing" ? ongoingContests : previousContests;

  return (
    <div className="h-svh w-full bg-slate-100 font-gilroy-regular flex flex-col">
      <Header />

      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto scrollbar-thumb-gray-500 pb-6 h-full"
      >
        <div className="text-center text-white text-[12px] font-gilroy-medium bg-[#008080] py-1">You get 15% off on your first payment</div>
        <section className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-1">
          <div className="flex flex-col w-full">
            <div className="flex flex-row items-center justify-between">
              <h2 className="text-2xl text-primary font-semibold mb-1 px-2">
                Competitions
              </h2>
              <button type="button" 
                onClick={() => redirectToStore("contest_list")}
                className="bg-teal-600 py-0.5 px-3 text-white text-base font-medium rounded-md">
                Download App
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-2 px-2">
              <button
                className={`px-3 py-2 rounded-full text-sm text-teal-700 border md:text-base ${
                  contestType === "Ongoing"
                    ? "bg-white border-teal-700"
                    : "border-transparent"
                }`}
                onClick={() => setContestType("Ongoing")}
              >
                <span>Ongoing</span>
                {urlParamSubSkill && <span className="ml-1.5 font-gilroy-bold">{urlParamSubSkill}</span>}
                {ongoingContestCount && <span className="ml-1.5 px-1 pb-0.5 pt-1 rounded-md bg-[#008080] text-white">{ongoingContestCount}</span>}
              </button>

              <div
                className={`flex-row items-center px-3 py-2 rounded-full text-sm text-teal-700 border md:text-base ${
                  contestType === "Previous"
                    ? "bg-white border-teal-700"
                    : "border-transparent"
                }`}
                onClick={() => setContestType("Previous")}
              >
                <span>Previous</span>
                {urlParamSubSkill && <span className="ml-1.5 font-gilroy-bold">{urlParamSubSkill}</span>}
                {endedContestCount && <span className="ml-1.5 px-1 pb-0.5 pt-1 rounded-md bg-[#008080] text-white">{endedContestCount}</span>}
              </div>
            </div>

            <div className="space-y-2">
              {contestsToShow.length > 0 ? (
                <>
                  {contestsToShow.map((item) => (
                    <div
                      key={item?._id}
                      onClick={() => openContestPage(item)}
                      className="rounded-xl bg-white shadow cursor-pointer"
                    >
                      <ContestCard contest={item} />
                    </div>
                  ))}

                  {loading && (
                    <div className="flex items-center justify-center w-full mt-4">
                      <div className="h-6 w-6 border-2 border-teal-600 border-t-white animate-spin rounded-full" />
                    </div>
                  )}
                  {isEndOfList && (
                    <div className="flex items-center justify-center w-full mt-4">
                      <p className="py-2 text-sm md:text-base">End of List</p>
                    </div>
                  )}
                </>
              ) : (
                <p className="font-medium text-center text-teal-700">
                  No Contest Found
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Competition;
